import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { collection, doc, getDoc, getDocs, serverTimestamp, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { useHistory, useParams } from "react-router-dom";
import Navbar from "../Components/Navbar/navbar";
import CabecalhoDetalhes from "../Components/CabecalhoDetalhes/CabecalhoDetalhes";
import useEstadoSalvamento from "../Utils/useEstadoSalvamento";
import { auth, db, storage } from "../Config/firebase";
import { consultaTenantAtual, getUsuarioAtualComTenant } from "../Utils/tenant";
import RelatorioLocacaoEquipamentosPrint from "./RelatorioLocacaoEquipamentosPrint";
import { atualizarDocumentoComMesclagem } from "../Utils/concorrenciaFirestore";

function novoItem() {
  return {
    id: `item-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    nomeLocacao: "",
    quantidade: 1,
    unidade: "Mensal",
    equipamentoIds: [],
    equipamentos: [],
    acessorios: "",
    observacao: "",
  };
}

function normalizarLocacao(dados = {}) {
  return {
    ...dados,
    valorLocacao: dados.valorLocacao ?? "",
    responsavelRetirada: dados.responsavelRetirada || "",
    documentoResponsavelRetirada: dados.documentoResponsavelRetirada || "",
    telefoneResponsavelRetirada: dados.telefoneResponsavelRetirada || "",
    clienteAssinaturaNome: dados.clienteAssinaturaNome || "",
    clienteAssinaturaCargo: dados.clienteAssinaturaCargo || "",
    termoAssinado: dados.termoAssinado || null,
    observacoes: dados.observacoes || "",
    itens: Array.isArray(dados.itens) && dados.itens.length
      ? dados.itens.flatMap((item) => {
        const normalizado = { ...novoItem(), ...item };
        const salvos = Array.isArray(item.equipamentos) ? item.equipamentos : [];
        const ids = Array.isArray(item.equipamentoIds) ? item.equipamentoIds : salvos.map((equipamento) => equipamento.id);
        if (!ids.length) return [{ ...normalizado, equipamentoIds: [], equipamentos: salvos }];
        return ids.map((id, indice) => ({
          ...normalizado,
          id: indice === 0 ? normalizado.id : novoItem().id,
          equipamentoIds: [id],
          equipamentos: salvos.filter((equipamento) => equipamento.id === id),
        }));
      })
      : [novoItem()],
  };
}

export default function DetalhesLocacaoEquipamentos() {
  const { locacaoId } = useParams();
  const history = useHistory();
  const { salvandoCadastro, executarSalvamento } = useEstadoSalvamento();
  const [locacao, setLocacao] = useState(null);
  const [notificacao, setNotificacao] = useState(null);
  const [equipamentos, setEquipamentos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [modoPrint, setModoPrint] = useState(false);
  const [arquivoTermoAssinado, setArquivoTermoAssinado] = useState(null);
  const [enviandoTermoAssinado, setEnviandoTermoAssinado] = useState(false);
  const locacaoOriginalRef = useRef(null);

  useEffect(() => {
    carregarDados();
  }, [locacaoId]);

  async function carregarDados() {
    setCarregando(true);
    try {
      const [locacaoSnap, equipamentosSnap] = await Promise.all([
        getDoc(doc(db, "tblLocacoesEquipamentos", locacaoId)),
        getDocs(consultaTenantAtual(collection(db, "equipamentos"))),
      ]);

      if (!locacaoSnap.exists()) {
        alert("Locação não encontrada.");
        history.push("/app/locacao-equipamentos");
        return;
      }

      const locacaoCarregada = normalizarLocacao({ id: locacaoSnap.id, ...locacaoSnap.data() });
      setLocacao(locacaoCarregada);
      locacaoOriginalRef.current = locacaoCarregada;
      setEquipamentos(
        equipamentosSnap.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"))
      );
    } finally {
      setCarregando(false);
    }
  }

  const equipamentosDisponiveis = useMemo(
    () => equipamentos.filter((item) => {
      if (item.statusEquipamento === "descartado") return false;
      return item.tipoUso === "locacao" && item.locado !== true;
    }),
    [equipamentos]
  );

  function atualizarCampo(campo, valor) {
    setLocacao((anterior) => ({ ...anterior, [campo]: valor }));
  }

  function atualizarItem(indice, alteracoes) {
    setLocacao((anterior) => ({
      ...anterior,
      itens: anterior.itens.map((item, itemIndex) =>
        itemIndex === indice ? { ...item, ...alteracoes } : item
      ),
    }));
  }

  function atualizarEquipamento(indice, equipamento) {
    const digitado = typeof equipamento === "string";
    atualizarItem(indice, {
      equipamentoIds: equipamento && !digitado ? [equipamento.id] : [],
      equipamentos: equipamento ? [{
        id: digitado ? "" : equipamento.id,
        nome: digitado ? equipamento : equipamento.nome || "",
        descricao: digitado ? "" : equipamento.descricao || "",
        tipoUso: digitado ? "" : equipamento.tipoUso || "",
      }] : [],
    });
  }

  function adicionarItem() {
    setLocacao((anterior) => ({ ...anterior, itens: [...anterior.itens, novoItem()] }));
  }

  function removerItem(indice) {
    setLocacao((anterior) => ({
      ...anterior,
      itens: anterior.itens.length === 1
        ? [novoItem()]
        : anterior.itens.filter((_, itemIndex) => itemIndex !== indice),
    }));
  }

  async function enviarTermoAssinado() {
    if (!arquivoTermoAssinado) {
      alert("Selecione a imagem ou o PDF do termo assinado.");
      return;
    }

    const tiposPermitidos = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
    if (!tiposPermitidos.includes(arquivoTermoAssinado.type)) {
      alert("Selecione um arquivo JPG, PNG, WebP ou PDF.");
      return;
    }

    const limiteBytes = arquivoTermoAssinado.type === "application/pdf"
      ? 100 * 1024 * 1024
      : 5 * 1024 * 1024;
    if (arquivoTermoAssinado.size >= limiteBytes) {
      alert(arquivoTermoAssinado.type === "application/pdf"
        ? "O PDF deve ter menos de 100 MB."
        : "A imagem deve ter menos de 5 MB.");
      return;
    }

    setEnviandoTermoAssinado(true);
    try {
      const usuario = await getUsuarioAtualComTenant();
      if (!usuario.tenantId) throw new Error("USUARIO_SEM_TENANT_ID");

      const storagePath = `tenants/${usuario.tenantId}/locacoes-equipamentos/${locacaoId}/termo-assinado`;
      const referenciaStorage = ref(storage, storagePath);
      await uploadBytes(referenciaStorage, arquivoTermoAssinado, {
        contentType: arquivoTermoAssinado.type,
      });
      const url = await getDownloadURL(referenciaStorage);
      const termoAssinado = {
        storagePath,
        url,
        nomeArquivo: arquivoTermoAssinado.name,
        tipo: arquivoTermoAssinado.type,
        tamanho: arquivoTermoAssinado.size,
        enviadoEm: new Date().toISOString(),
        enviadoPor: auth.currentUser?.uid || null,
        enviadoPorEmail: auth.currentUser?.email || "",
      };

      await updateDoc(doc(db, "tblLocacoesEquipamentos", locacaoId), {
        termoAssinado,
        atualizadoEm: serverTimestamp(),
        atualizadoPor: auth.currentUser?.uid || null,
        atualizadoPorEmail: auth.currentUser?.email || "",
        atualizadoPorNome: auth.currentUser?.displayName || "",
      });

      const locacaoAtualizada = { ...locacao, termoAssinado };
      setLocacao(locacaoAtualizada);
      locacaoOriginalRef.current = locacaoAtualizada;
      setArquivoTermoAssinado(null);
    } catch (error) {
      console.error("Erro ao enviar termo de locação assinado:", error);
      alert("Não foi possível enviar o termo assinado.");
    } finally {
      setEnviandoTermoAssinado(false);
    }
  }

  async function abrirTermoAssinado() {
    try {
      const termoAssinado = locacao.termoAssinado;
      if (!termoAssinado?.storagePath && !termoAssinado?.url) return;
      const url = termoAssinado.url || await getDownloadURL(ref(storage, termoAssinado.storagePath));
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Erro ao abrir termo de locação assinado:", error);
      alert("Não foi possível abrir o termo assinado.");
    }
  }

  async function salvar() {
    if (locacao.valorLocacao !== "" && (!Number.isFinite(Number(locacao.valorLocacao)) || Number(locacao.valorLocacao) < 0)) {
      alert("Informe um valor da locação válido, maior ou igual a zero.");
      return;
    }
    const itensValidos = locacao.itens.filter((item) => item.nomeLocacao.trim());
    if (itensValidos.some((item) => !Number.isFinite(Number(String(item.quantidade).replace(",", "."))) ||
        Number(String(item.quantidade).replace(",", ".")) <= 0 || !item.unidade)) {
      alert("Informe uma quantidade maior que zero e o tipo de cada item da locação.");
      return;
    }
    if (!itensValidos.length || itensValidos.some((item) =>
      item.equipamentoIds.length !== 1 &&
      !(item.equipamentoIds.length === 0 && item.equipamentos.length === 1 && item.equipamentos[0].nome?.trim())
    )) {
      alert("Informe cada item da locação e selecione ou digite um equipamento por linha.");
      return;
    }

    setNotificacao(null);
    try {
      await executarSalvamento(async () => {
      const usuarioAtual = auth.currentUser;
      const referencia = doc(db, "tblLocacoesEquipamentos", locacaoId);
      await atualizarDocumentoComMesclagem({
        db,
        referencia,
        original: locacaoOriginalRef.current,
        atual: { ...locacao, itens: itensValidos },
        preparar: (estado) => {
          const preparado = { ...estado };
          delete preparado.id;
          return preparado;
        },
        metadados: {
          atualizadoEm: serverTimestamp(),
          atualizadoPor: usuarioAtual?.uid || null,
          atualizadoPorEmail: usuarioAtual?.email || "",
          atualizadoPorNome: usuarioAtual?.displayName || "",
        },
      });
      const equipamentoIdsSelecionados = Array.from(
        new Set(itensValidos.flatMap((item) => item.equipamentoIds || []).filter(Boolean))
      );

      await Promise.all(
        equipamentoIdsSelecionados.map((equipamentoId) =>
          updateDoc(doc(db, "equipamentos", equipamentoId), { locado: true })
        )
      );
      setEquipamentos((atuais) =>
        atuais.map((equipamento) =>
          equipamentoIdsSelecionados.includes(equipamento.id)
            ? { ...equipamento, locado: true }
            : equipamento
        )
      );
      const snapshotAtualizado = await getDoc(referencia);
      const locacaoAtualizada = normalizarLocacao({
        id: snapshotAtualizado.id,
        ...snapshotAtualizado.data(),
      });
      setLocacao(locacaoAtualizada);
      locacaoOriginalRef.current = locacaoAtualizada;
      setNotificacao({ tipo: "success", texto: "Locação salva com sucesso." });
      });
    } catch (erro) {
      console.error("Erro ao salvar locação de equipamentos:", erro);
      setNotificacao({ tipo: "error", texto: "Não foi possível salvar a locação." });
    }
  }

  if (carregando || !locacao) {
    return (
      <>
        <Navbar />
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      </>
    );
  }

  if (modoPrint) {
    return <RelatorioLocacaoEquipamentosPrint locacao={locacao} onVoltar={() => setModoPrint(false)} />;
  }

  return (
    <>
      <Navbar />
      <Box p={{ xs: 2, md: 4 }}>
        <CabecalhoDetalhes
          titulo="Detalhes da locação de equipamentos"
          subtitulo={`${locacao.codigo || "Sem código"} — ${locacao.clienteNome || "Sem cliente"}`}
        >
          <Button variant="outlined" onClick={() => history.push("/app/locacao-equipamentos")}>Voltar</Button>
          <Button variant="outlined" startIcon={<VisibilityIcon />} onClick={() => setModoPrint(true)}>Visualizar relatório</Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={salvar} disabled={salvandoCadastro}>
            {salvandoCadastro ? "Salvando..." : "Salvar"}
          </Button>
        </CabecalhoDetalhes>

        {notificacao && (
          <Alert severity={notificacao.tipo} sx={{ mb: 2 }}>
            {notificacao.texto}
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" fontWeight={700} mb={2}>Dados da locação</Typography>
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField label="Código" value={locacao.codigo || ""} InputProps={{ readOnly: true }} sx={{ width: { md: 180 } }} />
              <TextField label="Cliente" value={locacao.clienteNome || ""} InputProps={{ readOnly: true }} fullWidth />
              <TextField label="Obra" value={locacao.obraNome || ""} InputProps={{ readOnly: true }} fullWidth />
              <TextField select label="Status" value={locacao.status || "planejada"} onChange={(e) => atualizarCampo("status", e.target.value)} sx={{ width: { md: 180 } }}>
                <MenuItem value="planejada">Planejada</MenuItem>
                <MenuItem value="ativa">Ativa</MenuItem>
                <MenuItem value="devolvida">Devolvida</MenuItem>
                <MenuItem value="cancelada">Cancelada</MenuItem>
              </TextField>
            </Stack>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField type="date" label="Data inicial" InputLabelProps={{ shrink: true }} value={locacao.dataInicio || ""} onChange={(e) => atualizarCampo("dataInicio", e.target.value)} fullWidth />
              <TextField type="date" label="Data final (opcional)" helperText="Pode ficar em branco enquanto a locação estiver em andamento." InputLabelProps={{ shrink: true }} value={locacao.dataFim || ""} onChange={(e) => atualizarCampo("dataFim", e.target.value)} fullWidth />
              <TextField label="Valor total da locação (R$)" type="number" value={locacao.valorLocacao}
                onChange={(e) => atualizarCampo("valorLocacao", e.target.value === "" ? "" : Number(e.target.value))}
                inputProps={{ min: 0, step: "0.01" }} helperText="Uso interno e medição. Não aparece no relatório de locação." fullWidth />
            </Stack>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} gap={1} mb={2}>
            <Typography variant="h6" fontWeight={700}>Itens da locação</Typography>
          </Stack>

          <TableContainer>
          <Table size="small" sx={{
            minWidth: 1100,
            tableLayout: "fixed",
            "& .MuiTableCell-root": { px: 1 },
            "& .MuiOutlinedInput-notchedOutline": { border: 0 },
            "& .MuiOutlinedInput-root.Mui-focused": { outline: "2px solid", outlineColor: "primary.main" },
          }}>
            <colgroup>
              <col style={{ width: "18%" }} />
              <col style={{ width: "26%" }} />
              <col style={{ width: 80 }} />
              <col style={{ width: 110 }} />
              <col />
              <col />
              <col style={{ width: 60 }} />
            </colgroup>
            <TableHead><TableRow>
              <TableCell>Item da locação</TableCell>
              <TableCell>Equipamento</TableCell>
              <TableCell>Qtd.</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Acessórios</TableCell>
              <TableCell>Observações</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow></TableHead>
            <TableBody>
            {locacao.itens.map((item, indice) => {
              const salvos = Array.isArray(item.equipamentos) ? item.equipamentos : [];
              const selecionados = item.equipamentoIds.map((id) =>
                equipamentos.find((equipamento) => equipamento.id === id) || salvos.find((equipamento) => equipamento.id === id)
              ).filter(Boolean);
              const opcoes = [...equipamentosDisponiveis];
              selecionados.forEach((selecionado) => {
                if (!opcoes.some((opcao) => opcao.id === selecionado.id)) opcoes.push(selecionado);
              });

              return (
                <TableRow key={`${item.id}-${indice}`} sx={{ verticalAlign: "top" }}>
                  <TableCell>
                    <TextField
                      size="small"
                      inputProps={{ "aria-label": "Item da locação" }}
                      placeholder="Ex.: Kit Slump"
                      value={item.nomeLocacao || ""}
                      onChange={(e) => atualizarItem(indice, { nomeLocacao: e.target.value })}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <Autocomplete
                      size="small"
                      freeSolo
                      fullWidth
                      options={opcoes}
                      value={selecionados[0] || salvos[0]?.nome || null}
                      isOptionEqualToValue={(option, value) => typeof value !== "string" && option.id === value.id}
                      getOptionLabel={(option) => typeof option === "string" ? option : [option.nome, option.descricao].filter(Boolean).join(" - ")}
                      onChange={(_, equipamento) => atualizarEquipamento(indice, equipamento)}
                      onInputChange={(_, texto, motivo) => {
                        if (motivo === "input") atualizarEquipamento(indice, texto);
                      }}
                      renderInput={(params) => <TextField {...params} placeholder="Selecionar ou digitar equipamento" inputProps={{ ...params.inputProps, "aria-label": "Equipamento" }} />}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField fullWidth size="small" value={item.quantidade ?? 1}
                      onChange={(e) => atualizarItem(indice, { quantidade: e.target.value })}
                      inputProps={{ inputMode: "decimal", "aria-label": "Quantidade" }} />
                  </TableCell>
                  <TableCell>
                    <TextField select fullWidth size="small" value={item.unidade || "Mensal"}
                      onChange={(e) => atualizarItem(indice, { unidade: e.target.value })}
                      inputProps={{ "aria-label": "Tipo" }}>
                      {["Mensal", "Diária", "Semanal", "Hora", "Unidade"].map((unidade) =>
                        <MenuItem key={unidade} value={unidade}>{unidade}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell>
                      <TextField
                        size="small"
                        inputProps={{ "aria-label": "Acessórios / partes do kit" }}
                        placeholder="Ex.: placa, cone, concha e haste"
                        value={item.acessorios || ""}
                        onChange={(e) => atualizarItem(indice, { acessorios: e.target.value })}
                        multiline
                        minRows={2}
                        fullWidth
                      />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      inputProps={{ "aria-label": "Observação do item" }}
                      placeholder="Ex.: item de desgaste; reposição por conta do locatário"
                      value={item.observacao || ""}
                      onChange={(e) => atualizarItem(indice, { observacao: e.target.value })}
                      multiline
                      minRows={2}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Remover item">
                      <IconButton color="error" onClick={() => removerItem(indice)}><DeleteIcon /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
            </TableBody>
          </Table>
          </TableContainer>
          <Button startIcon={<AddIcon />} onClick={adicionarItem} sx={{ mt: 1 }}>Adicionar item</Button>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" fontWeight={700} mb={2}>Retirada e assinatura do cliente</Typography>
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField fullWidth label="Responsável pela retirada" value={locacao.responsavelRetirada} onChange={(e) => atualizarCampo("responsavelRetirada", e.target.value)} />
              <TextField fullWidth label="CPF / Documento" value={locacao.documentoResponsavelRetirada} onChange={(e) => atualizarCampo("documentoResponsavelRetirada", e.target.value)} />
              <TextField fullWidth label="Telefone" value={locacao.telefoneResponsavelRetirada} onChange={(e) => atualizarCampo("telefoneResponsavelRetirada", e.target.value)} />
            </Stack>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField fullWidth label="Nome do cliente para assinatura" value={locacao.clienteAssinaturaNome} onChange={(e) => atualizarCampo("clienteAssinaturaNome", e.target.value)} />
              <TextField fullWidth label="Cargo / Função" value={locacao.clienteAssinaturaCargo} onChange={(e) => atualizarCampo("clienteAssinaturaCargo", e.target.value)} />
            </Stack>
            <TextField multiline minRows={3} label="Observações" value={locacao.observacoes} onChange={(e) => atualizarCampo("observacoes", e.target.value)} />
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" fontWeight={700}>Termo assinado digitalizado</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
            Anexe o termo após a assinatura do cliente. Formatos aceitos: JPG, PNG, WebP ou PDF.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
            <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} disabled={enviandoTermoAssinado}>
              Selecionar arquivo
              <input
                hidden
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                onChange={(event) => setArquivoTermoAssinado(event.target.files?.[0] || null)}
              />
            </Button>
            <Typography variant="body2" sx={{ flex: 1 }}>
              {arquivoTermoAssinado?.name || locacao.termoAssinado?.nomeArquivo || "Nenhum termo anexado"}
            </Typography>
            {locacao.termoAssinado?.storagePath || locacao.termoAssinado?.url ? (
              <Button variant="outlined" startIcon={<OpenInNewIcon />} onClick={abrirTermoAssinado}>
                Abrir termo assinado
              </Button>
            ) : null}
            <Button
              variant="contained"
              startIcon={<UploadFileIcon />}
              onClick={enviarTermoAssinado}
              disabled={!arquivoTermoAssinado || enviandoTermoAssinado}
            >
              {enviandoTermoAssinado ? "Enviando..." : locacao.termoAssinado ? "Substituir anexo" : "Salvar anexo"}
            </Button>
          </Stack>
        </Paper>
      </Box>
    </>
  );
}
